import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CButton, CCol, CFormInput, CRow } from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faEdit, faEye, faTrash } from "@fortawesome/free-solid-svg-icons";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";

import TNTable from "@/components/TNTable";
import CustomDropdown from "@/components/CustomDropdown";
import { useTranslationContext } from "@/context/TranslationContext";
import { dummyUser } from "@/constants/translation/dummyUser.translation";
import { breadcrumb_key, messages } from "@/constants/translation/common.translation";
import { dummyUser as dummyUserRoutes } from "@/libs/routes";
import constants from "@/libs/constant";
import { setFormatDate } from "@/utils/dateFormate";
import { useListDummyUser, useDummyUserDelete, useUpdateDummyUserStatus } from "@/hooks/useDummyUser";

const ONLINE_STATUS_FILTER_VALUES = ["all", "online", "offline"];

// API responses pass every value through the app's shared encryption round-trip,
// which stringifies booleans (true -> "true", false -> "false"). Native JS truthiness
// on the raw value is wrong here since the string "false" is truthy.
const isOnlineValue = (value) => value === true || value === constants.status.apiStatus.true;

const ListDummyUserPage = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(constants.pagination.defaultPage);
  const [searchText, setSearchText] = useState("");
  const [tempSearchText, setTempSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const searchTimeout = useRef(null);

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  const handleSearch = (e) => {
    const value = e.target.value;
    setTempSearchText(value);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      setSearchText(value);
      setCurrentPage(1);
    }, 400);
  };

  const handleClearFilter = () => {
    setTempSearchText("");
    setSearchText("");
    setStatusFilter("all");
    setCurrentPage(constants.pagination.defaultPage);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === constants.sort.order.asc ? constants.sort.order.desc : constants.sort.order.asc);
    } else {
      setSortField(field);
      setSortOrder(constants.sort.order.asc);
    }
    setCurrentPage(1);
  };

  const {
    data: listData,
    isLoading,
    refetch,
  } = useListDummyUser({
    page: currentPage || constants.pagination.defaultPage,
    limit: constants.pagination.defaultPageSize,
    ...(searchText && searchText.trim() ? { search: searchText.trim() } : {}),
    ...(statusFilter && statusFilter !== "all" ? { online_status: statusFilter } : {}),
    sort_by: sortField,
    order_by: sortOrder,
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  const [dummyUserList, setDummyUserList] = useState([]);
  const [paginationData, setPaginationData] = useState(null);

  useEffect(() => {
    if (listData && !isLoading) {
      setDummyUserList(listData.data?.dummyUserList || []);
      setPaginationData(listData.data?.pagination);
    }
  }, [listData, isLoading]);

  const { mutate: doDeleteDummyUser } = useDummyUserDelete(
    (response) => {
      toast.success(response?.message || t(messages.toast.dummyUser.deleted));
      refetch();
    },
    (error) => {
      toast.error(error?.response?.data?.message || t(messages.toast.dummyUser.deleteFailed));
    }
  );

  const { mutate: doUpdateStatus } = useUpdateDummyUserStatus(
    (response) => {
      toast.success(response?.message || t(messages.toast.dummyUser.statusUpdated));
      refetch();
    },
    (error) => {
      toast.error(error?.response?.data?.message || t(messages.toast.dummyUser.statusUpdateFailed));
    }
  );

  const handleViewClick = (row) => {
    navigate(dummyUserRoutes.view.replace(":id", row.uuid));
  };

  const handleEditClick = (row) => {
    navigate(dummyUserRoutes.edit.replace(":id", row.uuid));
  };

  const handleDeleteClick = (row) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-box">
          <div className="alert-popup">
            <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
            <h2 className="alert-title">{t(dummyUser.list.delete_popup_heading_text)}</h2>
            <p className="text-center mb-3">{t(dummyUser.list.delete_popup_message_text)}</p>
            <div className="d-flex gap-2 justify-content-center">
              <CButton
                color="primary"
                className="table-delete-button"
                onClick={() => {
                  doDeleteDummyUser(row.uuid);
                  onClose();
                }}
              >
                {t(dummyUser.list.delete_popup_yes_btn_text)}
              </CButton>
              <CButton className="table-primary-button" color="danger" onClick={onClose}>
                {t(dummyUser.list.delete_popup_no_btn_text)}
              </CButton>
            </div>
          </div>
        </div>
      ),
    });
  };

  const handleStatusClick = (row) => {
    const nextStatus = !isOnlineValue(row.is_online);

    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-box">
          <div className="alert-popup">
            <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
            <h2 className="alert-title">{t(dummyUser.list.status_popup_heading_text)}</h2>
            <div className="d-flex gap-2 justify-content-center">
              <CButton
                color="primary"
                className="table-delete-button"
                onClick={() => {
                  doUpdateStatus({ uuid: row.uuid, isOnline: nextStatus });
                  onClose();
                }}
              >
                {t(dummyUser.list.status_popup_yes_btn_text)}
              </CButton>
              <CButton className="table-primary-button" color="danger" onClick={onClose}>
                {t(dummyUser.list.status_popup_no_btn_text)}
              </CButton>
            </div>
          </div>
        </div>
      ),
    });
  };

  const statusFilterOptions = useMemo(
    () =>
      ONLINE_STATUS_FILTER_VALUES.map((value) => ({
        value,
        label: t(dummyUser.list.status_filter_options[value]),
      })),
    [t]
  );

  const columns = useMemo(
    () => [
      {
        id: "created_at",
        accessorKey: "created_at",
        header: t(dummyUser.list.table.created_at_col_heading),
        cell: ({ getValue }) => <span>{setFormatDate(getValue())}</span>,
      },
      {
        id: "email",
        accessorKey: "email",
        header: () => (
          <div onClick={() => handleSort("email")} style={{ cursor: "pointer" }}>
            {t(dummyUser.list.table.email_col_heading)}
          </div>
        ),
        enableSorting: true,
        cell: ({ getValue }) => <span>{getValue() || t(dummyUser.list.defaults.na)}</span>,
      },
      {
        id: "full_name",
        accessorKey: "full_name",
        header: () => (
          <div onClick={() => handleSort("full_name")} style={{ cursor: "pointer" }}>
            {t(dummyUser.list.table.full_name_col_heading)}
          </div>
        ),
        enableSorting: true,
        cell: ({ row }) => <span>{row.original.full_name || t(dummyUser.list.defaults.na)}</span>,
      },
      {
        id: "username",
        accessorKey: "username",
        header: t(dummyUser.list.table.username_col_heading),
        cell: ({ getValue }) => <span>{getValue() || t(dummyUser.list.defaults.na)}</span>,
      },
      {
        id: "is_online",
        accessorKey: "is_online",
        header: t(dummyUser.list.table.status_col_heading),
        cell: ({ row }) => {
          const isOnline = isOnlineValue(row.original.is_online);
          return (
            <span
              className={`cursor-pointer ${isOnline ? "text-success" : "text-danger"}`}
              onClick={() => handleStatusClick(row.original)}
            >
              {isOnline ? t(dummyUser.list.status.online) : t(dummyUser.list.status.offline)}
            </span>
          );
        },
      },
      {
        id: "actions",
        accessorKey: "uuid",
        header: t(dummyUser.list.table.action_col_heading),
        cell: ({ row }) => (
          <div className="d-flex gap-2 justify-content-center">
            <div className="table-action-text" onClick={() => handleViewClick(row.original)}>
              <FontAwesomeIcon icon={faEye} className="list-icon" />
            </div>
            <div className="table-action-text" onClick={() => handleEditClick(row.original)}>
              <FontAwesomeIcon icon={faEdit} className="list-icon" />
            </div>
            <div className="table-action-text" onClick={() => handleDeleteClick(row.original)}>
              <FontAwesomeIcon icon={faTrash} className="list-icon" />
            </div>
          </div>
        ),
      },
    ],
    [t, handleSort]
  );

  return (
    <div className="dummy-user-list">
      <CRow className="list-align">
        <CCol lg={10} md={10} sm={12} xs={12} className="d-flex gap-2 justify-content-end flex-wrap">
          <CFormInput
            placeholder={t(dummyUser.list.search_placeholder)}
            type="text"
            value={tempSearchText}
            onChange={handleSearch}
            className="filter-field-space form-control-sm"
            style={{ maxWidth: "300px" }}
          />

          <CustomDropdown
            className="filter-field-space form-control-sm"
            style={{ 
              maxWidth: '200px', 
              backgroundColor: '#212631', 
              borderColor: '#212631', 
              color: 'white'
            }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            options={statusFilterOptions}
          />

          <CButton
            color="primary"
            type="button"
            className="filter-field-space"
            onClick={handleClearFilter}
            style={{ height: "45px", display: tempSearchText.trim() || statusFilter !== "all" ? "inline-block" : "none" }}
          >
            {t(dummyUser.list.clear_filter_btn_text)}
          </CButton>

          <CButton
            color="primary"
            type="button"
            className="filter-field-space"
            style={{ height: "45px"}}
            onClick={() => navigate(dummyUserRoutes.add)}
          >
            {t(dummyUser.list.create_btn_text)}
          </CButton>
        </CCol>
      </CRow>

      <TNTable
        columns={columns}
        data={dummyUserList}
        paginationData={paginationData}
        onSelectPage={(pageIndex) => {
          setCurrentPage(pageIndex + 1);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        pageIndexGet={currentPage - 1}
        isLoading={isLoading}
        t={t}
        responsive={false}
        className="dummy-user-table"
      />
    </div>
  );
};

ListDummyUserPage.breadCrumbArray = [
  {
    name: breadcrumb_key.dummyUser?.list,
    pathname: dummyUserRoutes.list,
    active: true,
  },
];

export default ListDummyUserPage;
